import { WINE_COLORS, type WineCategoryInput, type WineCategoryView } from '@gwc/shared';
import { WINE_COLOR_LABELS, isWineColor } from '../api/rsvp';

const MAX_CATEGORIES = 12;

/**
 * The tasting's wine categories ("Reds, max 5" / "Whites, max 3"). A member
 * picks one on the RSVP and a full one is closed to them. Positions follow the
 * order on screen.
 */
export function WineCategoriesEditor({
  value,
  onChange,
  usage = [],
  disabled,
}: {
  value: WineCategoryInput[];
  onChange: (v: WineCategoryInput[]) => void;
  /** The saved categories with their counts (edit only). */
  usage?: WineCategoryView[];
  disabled?: boolean;
}) {
  const setAt = (i: number, patch: Partial<WineCategoryInput>) =>
    onChange(value.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const removeAt = (i: number) => onChange(value.filter((_, j) => j !== i));
  const move = (i: number, by: -1 | 1) => {
    const j = i + by;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };

  const used = (id?: string) => (id ? usage.find((u) => u.id === id)?.used ?? 0 : 0);

  return (
    <div className="space-y-3">
      {value.length === 0 ? (
        <div className="flex flex-wrap items-center gap-3 text-sm text-cream-300/60">
          <span>No categories: members bring any wine.</span>
          <button
            type="button"
            className="btn-ghost px-3 py-1 text-xs"
            disabled={disabled}
            onClick={() =>
              onChange([
                { name: 'Reds', maxWines: null, color: 'red' },
                { name: 'Whites', maxWines: null, color: 'white' },
              ])
            }
          >
            Start with Reds and Whites
          </button>
        </div>
      ) : (
        <ul className="space-y-2">
          {value.map((c, i) => {
            const n = used(c.id);
            return (
              <li
                key={c.id ?? `new-${i}`}
                className="grid grid-cols-[1fr_auto] gap-2 rounded-md border border-ink-800 p-3 sm:grid-cols-[1fr_7rem_8rem_auto] sm:items-end"
              >
                <label className="col-span-2 block sm:col-span-1">
                  <span className="mb-1 block text-xs uppercase tracking-wide text-cream-300/70">
                    Category
                  </span>
                  <input
                    className="field"
                    value={c.name}
                    maxLength={80}
                    placeholder="Reds"
                    disabled={disabled}
                    onChange={(e) => setAt(i, { name: e.target.value })}
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs uppercase tracking-wide text-cream-300/70">
                    Max wines
                  </span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={20}
                    className="field"
                    placeholder="No limit"
                    disabled={disabled}
                    value={c.maxWines ?? ''}
                    onChange={(e) =>
                      setAt(i, { maxWines: e.target.value ? Number(e.target.value) : null })
                    }
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs uppercase tracking-wide text-cream-300/70">
                    Colour
                  </span>
                  <select
                    className="field"
                    disabled={disabled}
                    value={c.color ?? ''}
                    onChange={(e) =>
                      setAt(i, { color: isWineColor(e.target.value) ? e.target.value : null })
                    }
                  >
                    <option value="">Any</option>
                    {WINE_COLORS.map((col) => (
                      <option key={col} value={col}>
                        {WINE_COLOR_LABELS[col]}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="col-span-2 flex items-center justify-end gap-1 sm:col-span-1">
                  <button
                    type="button"
                    className="btn-quiet px-2 py-1 text-xs"
                    aria-label={`Move ${c.name || 'category'} up`}
                    disabled={disabled || i === 0}
                    onClick={() => move(i, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="btn-quiet px-2 py-1 text-xs"
                    aria-label={`Move ${c.name || 'category'} down`}
                    disabled={disabled || i === value.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="btn-quiet px-2 py-1 text-xs hover:text-wine-300"
                    disabled={disabled}
                    onClick={() => removeAt(i)}
                  >
                    Remove
                  </button>
                </div>
                {n > 0 && (
                  <p className="col-span-2 text-xs text-cream-300/50 sm:col-span-4">
                    {n} {n === 1 ? 'person has' : 'people have'} picked this.
                    {c.maxWines != null && c.maxWines < n && ' They keep their seats.'}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {value.length > 0 && value.length < MAX_CATEGORIES && (
        <button
          type="button"
          className="btn-ghost px-3 py-1 text-xs"
          disabled={disabled}
          onClick={() => onChange([...value, { name: '', maxWines: null, color: null }])}
        >
          + Add category
        </button>
      )}
      {usage.some((u) => u.used > 0 && !value.some((c) => c.id === u.id)) && (
        <p className="text-xs text-warning">
          Removing a category people have picked keeps their seats; they just lose the category.
        </p>
      )}
    </div>
  );
}
